/**
 * 자산현황 시계열 — 현금 / 재고자산 / 정산대기 일자별 값
 *
 * 판매장부 「자산현황」 차트(AssetTrendChart.vue)와 대시보드 자산 타일이 **같은 함수**를 부른다.
 * 식을 두 화면에 복붙하면 한쪽만 고쳐졌을 때 두 화면 숫자가 조용히 갈린다.
 * (lotCost.js 와 같은 이유로 CommonJS — webpack `import` 로도 node `require` 로도 읽힌다)
 *
 * 세 값 모두 '기준점에서 앞뒤로 감는' 방식이다.
 *   재고자산 : 상품별 재고 스냅샷(InventoryCoverage.computedAt)이 기준점.
 *              스냅샷 시점이 상품마다 다르므로 상품별로 각자 감는다.
 *   현금     : 사용자가 입력한 기준일 잔고가 기준점.
 *   정산대기 : 판매 시점에 쌓이고 정산 지급일에 빠지는 큐.
 *
 * 근거·한계는 docs/sales-ledger-pnl-design.md 참고.
 */
const { lotPriceByInboundId } = require('./lotCost');

function num(v) {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
}

function ymd(d) {
    return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
}

function fillDays(from, to) {
    const out = [];
    const cur = new Date(from + 'T00:00:00');
    const end = new Date(to + 'T00:00:00');
    let guard = 0;
    while (cur <= end && guard++ < 2000) {
        out.push(ymd(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
}

function addDays(date, days) {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return ymd(d);
}

function lastDayOf(month) {
    const y = parseInt(month.slice(0, 4), 10);
    const m = parseInt(month.slice(5, 7), 10);
    const d = new Date(y, m, 0);
    return month + '-' + String(d.getDate()).padStart(2, '0');
}

/**
 * @param {object} input
 *   dailyData      서버 일별 집계(getLedgerMonthlySummary.days)
 *   fixedCosts     고정비 [{ month, items:[{amount}] }]
 *   productCosts   상품 직접비 [{ date, amount }]
 *   setting        { baseCashDate, baseCashAmount }
 *   coverage       상품별 재고 스냅샷 [{ vendorItemId, totalStockAmount, purchasePrice, leadTime, ... }]
 *   inbound        입고이력 [{ shipmentId, stowedDate, skus:[{vendorItemId, stowedQty}] }]
 *   schedules      실제 정산 일정 [{ periodStart, periodEnd, settlementDate, cycle }]
 *   purchaseLots   상품별 매입 lot [{ vendorItemId, 매입정보 }]
 *   payoutLagDays  정산주기 마감일 → 실제 지급까지 일수 (기본 15)
 *   defaultLeadDays 상품별 리드타임을 모를 때 (기본 20)
 */
/**
 * 정산 일정 조회 — 그 날짜(매출인식일)가 속한 정산 기간의 실제 입금일.
 * 자산현황(아래 buildAssetSeries)과 재고 대사의 「보상 입금 대기」(inventory-services/growth-recon.js)가
 * 같은 규칙을 쓰도록 꺼내 둔다. 두 벌로 두면 입금일이 화면마다 갈린다.
 */
function scheduleLookup(schedules) {
    const sortedSchedules = (schedules || [])
        .filter(s => s && s.periodStart && s.periodEnd && s.settlementDate)
        .sort((a, b) => (a.periodEnd < b.periodEnd ? 1 : -1));
    const scheduleFor = date => {
        for (const s of sortedSchedules) {
            if (s.periodStart && s.periodEnd && date >= s.periodStart && date <= s.periodEnd) {
                return s.settlementDate;
            }
        }
        return null;
    };
    return { sortedSchedules, scheduleFor };
}

function buildAssetSeries(input) {
    const dailyData = input.dailyData || [];
    const fixedCosts = input.fixedCosts || [];
    const productCosts = input.productCosts || [];
    const setting = input.setting || null;
    const coverage = input.coverage || [];
    const inbound = input.inbound || [];
    const schedules = input.schedules || [];
    const purchaseLots = input.purchaseLots || [];
    const payoutLagDays = input.payoutLagDays != null ? input.payoutLagDays : 15;
    const defaultLeadDays = input.defaultLeadDays != null ? input.defaultLeadDays : 20;

    const hasSetting = !!(setting && setting.baseCashDate);

    // 데이터가 있는 전 구간의 날짜 목록 (회사마다 시작일이 다르므로 데이터에서 뽑는다)
    const dates = (() => {
        const set = new Set();
        dailyData.forEach(d => { if (d.date) set.add(d.date); });
        inbound.forEach(i => { if (i.stowedDate) set.add(i.stowedDate); });
        if (!set.size) return [];
        const sorted = Array.from(set).sort();
        return fillDays(sorted[0], sorted[sorted.length - 1]);
    })();

    // 일자별 판매·비용 흐름
    const flows = {};
    const ensure = d => {
        if (!flows[d]) flows[d] = { netSales: 0, gp: 0, qtyByVid: {}, cost: 0, purchase: 0, vat: 0 };
        return flows[d];
    };
    // 서버 일별 집계(getLedgerMonthlySummary.days) 기준. 선택한 연도 전체가 들어온다
    dailyData.forEach(d => {
        if (!d.date) return;
        const f = ensure(d.date);
        f.netSales += num(d.netSales);
        // 정산으로 실제 들어오는 돈 = 순매출 − 판매비용(수수료·물류·광고).
        // 서버 기여이익에는 기타비용이 이미 빠져 있는데, 그 돈은 쿠팡 정산이 아니라
        // 지출한 날 따로 나간다(아래 f.cost). 정산 입금액을 구하려면 되돌려 놓아야
        // 같은 돈을 두 번 빼지 않는다
        f.gp += num(d.contribution) + num(d.externalCost);
        f.purchase += num(d.cogs);
        f.vat += num(d.vat);
        // 그날 레코드에 찍힌 정산주기 (주정산↔월정산 변경이 그대로 반영됨)
        f.settlementType = d.settlementType || '';
        f.settlementCycleEnd = d.settlementCycleEnd || '';
    });
    productCosts.forEach(c => {
        if (!c.date) return;
        ensure(c.date).cost += num(c.amount);
    });
    // 고정비는 그 달 말일에 한 번 빠지는 것으로 본다
    fixedCosts.forEach(f => {
        const total = (f.items || []).reduce((s, it) => s + num(it.amount), 0);
        if (!total) return;
        ensure(lastDayOf(f.month)).cost += total;
    });

    // 입고 건별 금액 + 대금이 나간 날.
    //
    // 돈은 물건이 창고에 들어온 날이 아니라 '발주할 때' 나간다. 결제하고 나서
    // 생산·배송·통관을 거쳐 입고되기까지 걸리는 기간이 리드타임이고,
    // 그동안 돈은 이미 묶여 있다(운송중 재고). 그래서 지출 시점을 입고일에서
    // 리드타임만큼 앞으로 당긴다. 리드타임은 상품별 값(InventoryCoverage.leadTime)을 쓴다
    const inboundLots = (() => {
        const priceOf = {};
        const leadOf = {};
        coverage.forEach(c => {
            priceOf[String(c.vendorItemId)] = num(c.purchasePrice);
            leadOf[String(c.vendorItemId)] = num(c.leadTime);
        });
        // vendorItemId → { 입고ID: 그 lot 의 단가 }
        // coverage.purchasePrice 는 단일 대표값이라, lot 이 섞인 재고에 그걸 곱하면
        // 환율이 움직였을 때 자산 금액이 틀어진다(정본 §4). 입고ID 로 붙는 건 정확히 계산한다.
        const lotPriceOf = {};
        purchaseLots.forEach(p => {
            const m = lotPriceByInboundId(p.매입정보);
            if (Object.keys(m).length) lotPriceOf[String(p.vendorItemId)] = m;
        });

        const lots = [];
        inbound.forEach(i => {
            if (!i.stowedDate) return;
            let amt = 0;
            let leadSum = 0;
            let leadCnt = 0;
            const 입고ID = i.shipmentId ? String(i.shipmentId) : '';
            (i.skus || []).forEach(s => {
                const vid = String(s.vendorItemId);
                // 이 입고에 해당하는 lot 단가가 있으면 그걸 쓰고, 없으면 대표값 그대로
                const lotP = (입고ID && lotPriceOf[vid] && lotPriceOf[vid][입고ID]) || 0;
                amt += num(s.stowedQty) * (lotP || priceOf[vid] || 0);
                const l = leadOf[vid];
                if (l > 0) { leadSum += l; leadCnt++; }
            });
            if (!amt) return;
            const lead = leadCnt ? Math.round(leadSum / leadCnt) : defaultLeadDays;
            lots.push({ amount: amt, arriveDate: i.stowedDate, payDate: addDays(i.stowedDate, -lead) });
        });
        return lots;
    })();

    // 대금이 나간 날 기준 금액 (현금 흐름·재고 역산에 쓴다)
    const purchasePayByDate = {};
    inboundLots.forEach(l => {
        purchasePayByDate[l.payDate] = (purchasePayByDate[l.payDate] || 0) + l.amount;
    });

    // 입고이력이 덮는 시작일. 이 날짜 이후만 재고 역산이 성립한다
    const inboundFrom = (() => {
        const ds = inbound.map(i => i.stowedDate).filter(Boolean).sort();
        return ds.length ? ds[0] : null;
    })();

    // 재고자산 시계열 — 현재 스냅샷에서 뒤로 감는다.
    //
    // 기준이 되는 totalStockAmount(SCM의 '전체가용재고')는 구매중·배대지·통관중·입고중까지
    // 모두 포함한다. 즉 발주해서 대금이 나간 순간부터 이미 재고자산이다.
    // 그래서 재고가 늘어나는 시점은 '창고 입고'가 아니라 '발주'이고, 역산도 그에 맞춘다.
    //   재고(어제) = 재고(오늘) + 오늘 판매된 원가 − 오늘 발주한 금액
    //
    // 입고이력이 없는 구간에서 이 식을 돌리면 '팔기만 하고 들어온 건 없다'가 되어
    // 과거로 갈수록 재고가 무한정 커진다. 그래서 입고이력이 덮는 구간만 역산하고
    // 그 앞은 값을 유지한다
    const stockSeries = {};
    if (dates.length) {
        const snapshotTotal = coverage.reduce((s, c) => s + num(c.totalStockAmount), 0);
        let value = snapshotTotal;
        stockSeries[dates[dates.length - 1]] = value;
        for (let i = dates.length - 1; i > 0; i--) {
            const d = dates[i];
            if (inboundFrom && d > inboundFrom) {
                const f = flows[d] || {};
                value = value + num(f.purchase) - (purchasePayByDate[d] || 0);
                if (value < 0) value = 0;   // 기록 누락으로 음수가 되는 건 의미가 없다
            }
            stockSeries[dates[i - 1]] = value;
        }
    }

    // 정산 일정 — 기간이 있는 것만, 최신순으로 (scheduleLookup: 재고 대사 서버 계산도 같은 함수)
    const { sortedSchedules, scheduleFor } = scheduleLookup(schedules);

    // 이 셀러의 정산주기 (수집된 일정에서 가장 흔한 값)
    const settlementCycle = (() => {
        const count = {};
        schedules.forEach(s => {
            if (s.cycle) count[s.cycle] = (count[s.cycle] || 0) + 1;
        });
        const keys = Object.keys(count);
        if (!keys.length) return '';
        return keys.sort((a, b) => count[b] - count[a])[0];
    })();

    // 그 판매분이 실제로 입금되는 날
    //   정산주기(종료일)가 수집돼 있으면 그 마감일 + 지급지연일 → 주정산/월정산이 도중에 바뀌어도 따라간다
    //   과거 데이터엔 이 필드가 없으므로 없으면 매출인식일 + 기본 지연일로 근사한다
    const paydayOf = (date, flow) => {
        // 1순위 — 쿠팡이 알려준 실제 정산 일정. 판매일이 속한 정산 기간의 입금일을 그대로 쓴다
        const hit = scheduleFor(date);
        if (hit) return hit;

        // 2순위 — 레코드에 찍힌 정산주기 마감일 + 지급지연일
        const end = flow && flow.settlementCycleEnd ? String(flow.settlementCycleEnd).slice(0, 10).replace(/\./g, '-') : '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(end)) {
            return addDays(end, payoutLagDays);
        }

        // 3순위 — 아무 정보도 없을 때. 알려진 정산주기를 따라 마감일을 잡아 근사한다
        //         (정산은 매일 조금씩이 아니라 정해진 날에 뭉쳐 들어온다)
        if (settlementCycle === 'MONTHLY') {
            return addDays(lastDayOf(date.slice(0, 7)), payoutLagDays);
        }
        const d = new Date(date + 'T00:00:00');
        const toSunday = (7 - d.getDay()) % 7;
        return addDays(addDays(date, toSunday), payoutLagDays);
    };

    // 부가세는 분기마다 몰아서 낸다. 매출이 생길 때마다 조금씩 빠지는 게 아니라
    // 1~3월분을 4/25에, 4~6월분을 7/25에 내는 식이라 그날 현금이 크게 준다
    const vatPayByDate = {};
    (() => {
        const byQuarter = {};
        Object.keys(flows).forEach(d => {
            const v = num(flows[d].vat);
            if (!v) return;
            const y = parseInt(d.slice(0, 4), 10);
            const q = Math.ceil(parseInt(d.slice(5, 7), 10) / 3);
            const key = `${y}-${q}`;
            byQuarter[key] = (byQuarter[key] || 0) + v;
        });
        Object.keys(byQuarter).forEach(key => {
            const [y, q] = key.split('-').map(Number);
            // 분기 다음 달 25일 납부 (1분기 → 4/25)
            const payMonth = q * 3 + 1;
            const py = payMonth > 12 ? y + 1 : y;
            const pm = payMonth > 12 ? payMonth - 12 : payMonth;
            const date = `${py}-${String(pm).padStart(2, '0')}-25`;
            vatPayByDate[date] = (vatPayByDate[date] || 0) + byQuarter[key];
        });
    })();

    // 입금일별 정산 수령액 — 정산대기와 현금이 같은 규칙을 쓰도록 한 곳에서 만든다
    const incomeByDate = {};
    Object.keys(flows).forEach(d => {
        const f = flows[d];
        const amount = num(f.gp);
        if (!amount) return;
        const payday = paydayOf(d, f);
        incomeByDate[payday] = (incomeByDate[payday] || 0) + amount;
    });

    // 정산대기 시계열 — 판매일에 쌓이고 지급일에 빠진다
    const receivableSeries = {};
    (() => {
        let pending = 0;
        const queue = {};
        dates.forEach(d => {
            const f = flows[d] || {};
            const amount = num(f.gp);
            if (amount) {
                const payday = paydayOf(d, f);
                queue[payday] = (queue[payday] || 0) + amount;
                pending += amount;
            }
            // 지급일이 지난 건 큐에서 빠진다 (구간 밖으로 밀린 건도 함께 정리)
            Object.keys(queue).forEach(payday => {
                if (payday <= d) {
                    pending -= queue[payday];
                    delete queue[payday];
                }
            });
            receivableSeries[d] = pending;
        });
    })();

    // 현금 시계열 — 기준일 잔고에서 앞뒤로
    const cashSeries = {};
    if (!dates.length || !hasSetting) {
        dates.forEach(d => { cashSeries[d] = 0; });
    } else {
        const baseDate = setting.baseCashDate;
        const baseAmt = num(setting.baseCashAmount);

        // 그날의 현금 증감 = 정산입금 − 매입지출 − 비용
        // 입금일은 정산대기와 같은 규칙(paydayOf)으로 계산해야 두 라인이 어긋나지 않는다.
        //
        // 매입지출은 '입고된 금액'이 맞다. 돈은 물건이 들어올 때 나가지 팔릴 때 나가지 않는다.
        // 다만 입고 기록이 없는 구간에서는 지출이 0이 되어 현금이 과하게 늘어 보이므로,
        // 그때는 판매된 원가(cogs)로 대신한다 — 장기적으로는 매입과 판매가 같은 양이라
        // 흐름의 모양은 비슷하게 나온다
        const useInbound = !!inboundFrom;
        const deltaOf = d => {
            const f = flows[d] || {};
            // 매입 대금은 발주 시점에 나간다 (입고일 − 리드타임).
            // 입고 기록이 없는 구간만 판매원가로 대신한다
            const spend = (useInbound && d > inboundFrom)
                ? (purchasePayByDate[d] || 0)
                : num(f.purchase);
            return num(incomeByDate[d]) - spend - num(f.cost) - num(vatPayByDate[d]);
        };

        let baseIdx = dates.indexOf(baseDate);
        if (baseIdx === -1) {
            // 기준일이 구간 밖이면 가장 가까운 끝을 쓴다
            baseIdx = baseDate < dates[0] ? 0 : dates.length - 1;
        }

        cashSeries[dates[baseIdx]] = baseAmt;
        for (let i = baseIdx + 1; i < dates.length; i++) {
            cashSeries[dates[i]] = cashSeries[dates[i - 1]] + deltaOf(dates[i]);
        }
        for (let i = baseIdx - 1; i >= 0; i--) {
            cashSeries[dates[i]] = cashSeries[dates[i + 1]] - deltaOf(dates[i + 1]);
        }
    }

    const series = dates.map(d => {
        const cash = cashSeries[d] || 0;
        // 재고자산 — 운송중(구매중·배대지·통관중·입고중)까지 이미 포함된 값이다
        const stock = stockSeries[d] || 0;
        const receivable = receivableSeries[d] || 0;
        return { date: d, cash, stock, receivable, total: cash + stock + receivable };
    });

    return {
        hasSetting,
        dates,
        flows,
        inboundLots,
        purchasePayByDate,
        inboundFrom,
        stockSeries,
        sortedSchedules,
        settlementCycle,
        vatPayByDate,
        incomeByDate,
        receivableSeries,
        cashSeries,
        series
    };
}

/**
 * 「지난달 대비」 — 시계열 마지막 날과 그 전달 말일을 비교한다.
 *
 * 자산현황 차트의 마지막 점이 곧 '지금' 이다(차트 요약 카드와 같은 값).
 * 비교 기준을 30일 전이 아니라 전달 말일로 둔 이유: 차트 x축이 날짜라 월말 점이 눈에 잡히고,
 * 「8/31 대비」 처럼 기준일을 딱 떨어지게 적을 수 있다.
 *
 * 비교할 수 없는 경우는 null 로 돌려준다 — 0 으로 두면 「변화 없음」 으로 읽힌다.
 *   현금   : 기준 잔고를 입력하지 않았으면 계산 자체가 없다
 *   재고   : 비교일이 입고 기록 시작일보다 앞이면 그날 값은 역산이 아니라 그냥 채워둔 값이다
 */
function monthOverMonth(model) {
    const series = (model && model.series) || [];
    if (!series.length) return null;
    const last = series[series.length - 1];
    const baseDate = addDays(last.date.slice(0, 7) + '-01', -1);   // 전달 말일
    const base = series.find(s => s.date === baseDate) || null;

    const pick = (field, ok) => {
        if (!base || !ok) return { now: last[field], base: null, diff: null, rate: null };
        const diff = last[field] - base[field];
        // 기준값이 0 이하이면 % 는 뜻이 없다(0 → 나눗셈, 음수 → 부호가 뒤집힌다)
        const rate = base[field] > 0 ? diff / base[field] : null;
        return { now: last[field], base: base[field], diff, rate };
    };

    return {
        date: last.date,
        baseDate,
        cash: pick('cash', !!model.hasSetting),
        stock: pick('stock', !!model.inboundFrom && baseDate >= model.inboundFrom)
    };
}

module.exports = { buildAssetSeries, monthOverMonth, fillDays, addDays, lastDayOf, scheduleLookup };
